import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import clsx from 'clsx'
import ResponsiveImage from 'components/images/responsive-image'
import { isAppWebView } from 'lib/utils/common/commonUtils'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { POMELO_CARES_PAYPAL_WEBVIEW } from 'constants/cookies'
import env from 'config/env'
import Typo from 'constants/typography'
import Button from 'components/common/button'
import { BUTTON_TYPES } from 'constants/button'

const STATIC_CONTENTS = {
  pomeloCare: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/cares_logo_en.svg`,
      ratio: 0.426666667,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/cares_logo_en.svg`,
      ratio: 0.426666667,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/cares_logo_en.svg`,
      ratio: 0.426666667,
    },
  },
  lineBeKind: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_en.svg`,
      ratio: 0.235294118,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_en.svg`,
      ratio: 0.235294118,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_en.svg`,
      ratio: 0.235294118,
    },
  },
  lineBeKindPink: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_pink_en.svg`,
      ratio: 0.235294118,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_pink_en.svg`,
      ratio: 0.235294118,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_pink_en.svg`,
      ratio: 0.235294118,
    },
  },
  lineBeKindLong: {
    src: `${env.IMG_HOST}img/pomelo-cares/line_be_kind_long.svg`,
    ratio: 0.0822222222,
  },
  giveBack: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/give_back_en.svg`,
      ratio: 0.2406639,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/give_back_en.svg`,
      ratio: 0.2406639,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/give_back_en.svg`,
      ratio: 0.2406639,
    },
  },
  thankYou: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/thank_you_en.svg`,
      ratio: 0.263157895,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/thank_you_en.svg`,
      ratio: 0.263157895,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/thank_you_en.svg`,
      ratio: 0.263157895,
    },
  },
  thankYouLong: {
    src: `${env.IMG_HOST}img/pomelo-cares/thank_you_long.svg`,
    ratio: 0.0709219858,
  },
  iconsSet: {
    src: `${env.IMG_HOST}img/pomelo-cares/icons_set.svg`,
    ratio: 0.279069767,
  },
  lineSpreadLove: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_spread_love_en.svg`,
      ratio: 0.170731707,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_spread_love_en.svg`,
      ratio: 0.170731707,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/line_spread_love_en.svg`,
      ratio: 0.170731707,
    },
  },
  donateNow: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/donate_now_en.svg`,
      ratio: 0.12,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/donate_now_en.svg`,
      ratio: 0.12,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/donate_now_en.svg`,
      ratio: 0.12,
    },
  },
  clickDonate: {
    en: {
      src: `${env.IMG_HOST}img/pomelo-cares/click_donate_en.svg`,
      ratio: 1,
    },
    th: {
      src: `${env.IMG_HOST}img/pomelo-cares/click_donate_th.svg`,
      ratio: 1,
    },
    id: {
      src: `${env.IMG_HOST}img/pomelo-cares/click_donate_id.svg`,
      ratio: 1,
    },
  },
  paypalLinks: {
    th: {
      en: 'https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=CTRBJQYWTZGGE',
      th: 'https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=5RFK9CH4YGJ9A',
    },
    id: {
      id: 'https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=3ZBFTYX5ST8Q2',
    },
    sg: {
      en: 'https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=JBQNA6DEW822Y',
    },
    my: {
      en: 'https://www.paypal.com/cgi-bin/webscr?cmd=_s-xclick&hosted_button_id=3ZBFTYX5ST8Q2',
    },
  },
}

const YellowSection = ({
  amount,
  country,
  isDesktop,
  isLottieReady,
  isThankYouPage,
  language,
  t,
}) => {
  const jarHeartRef = useRef(null)
  const jarHeartAnimateRef = useRef(null)
  const [isWebview, setIsWebView] = useState(false)

  useEffect(() => {
    if (isLottieReady && !jarHeartAnimateRef.current) {
      jarHeartAnimateRef.current = window.bodymovin.loadAnimation({
        container: jarHeartRef.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: `${env.IMG_HOST}img/pomelo-cares/jar_heart.json`,
      })
    }
  }, [isLottieReady])

  useEffect(() => {
    if (isAppWebView()) {
      setIsWebView(true)
    }
  }, [])

  if (isThankYouPage) {
    return (
      <section className="pomelo-cares-yellow" style={{ paddingBottom: '1px' }}>
        <ResponsiveImage
          className="pomelo-care-logo"
          src={STATIC_CONTENTS.pomeloCare[language].src}
          imgRatio={STATIC_CONTENTS.pomeloCare[language].ratio}
          lazy={false}
        />
        <ResponsiveImage
          className="line-be-kind"
          src={
            isDesktop
              ? STATIC_CONTENTS.lineBeKindLong.src
              : STATIC_CONTENTS.lineBeKindPink[language].src
          }
          imgRatio={
            isDesktop
              ? STATIC_CONTENTS.lineBeKindLong.ratio
              : STATIC_CONTENTS.lineBeKindPink[language].ratio
          }
          lazy={false}
        />
        <ResponsiveImage
          className="give-back"
          src={
            isDesktop
              ? STATIC_CONTENTS.thankYouLong.src
              : STATIC_CONTENTS.thankYou[language].src
          }
          imgRatio={
            isDesktop
              ? STATIC_CONTENTS.thankYouLong.ratio
              : STATIC_CONTENTS.thankYou[language].ratio
          }
          lazy={false}
        />
        <span className={clsx(Typo.body2, 'text-give-back-body')}>
          {hasLocizeTranslation(
            t,
            'POMELO_CARE_THANK_YOU_BODY',
            `Your payment has been completed and you will shortly receive a confirmation email.

Your Donation will go towards these vital organisations, who do amazing work providing care and support for those affected by Covid-19.`,
          )}
        </span>
        <Link
          href={`/${country}/${language}/pomelocares${
            isWebview ? '?webview' : ''
          }`}
        >
          <Button link type={BUTTON_TYPES.outlinedLight} className="thx-cta">
            {hasLocizeTranslation(
              t,
              'POMELO_CARE_THANK_YOU_CTA',
              'track our progress here',
            )}
          </Button>
        </Link>
        <span className={clsx(Typo.subtitle2, 'text-thank-you-subbody')}>
          {hasLocizeTranslation(
            t,
            'POMELO_CARE_THANK_YOU_SUBBODY',
            `Thank you for supporting our Pomelo Cares initiatives.
We're all in this together.`,
          )}
        </span>
        <ResponsiveImage
          className="icons-set"
          src={STATIC_CONTENTS.iconsSet.src}
          imgRatio={STATIC_CONTENTS.iconsSet.ratio}
          lazy={false}
        />
        <span className={clsx(Typo.caption, 'text-give-back-hashtag')}>
          {hasLocizeTranslation(
            t,
            'POMELO_CARE_YELLOW_GIVE_BACK_HASHTAG',
            '#POMELOCARES',
          )}
        </span>
      </section>
    )
  }

  function onPaypalLinkClicked() {
    if (isWebview) {
      localStorage.setItem(POMELO_CARES_PAYPAL_WEBVIEW, 'true')
    }
  }

  return (
    <section className="pomelo-cares-yellow">
      <ResponsiveImage
        className="pomelo-care-logo"
        src={STATIC_CONTENTS.pomeloCare[language].src}
        imgRatio={STATIC_CONTENTS.pomeloCare[language].ratio}
        lazy={false}
      />
      <ResponsiveImage
        className="line-be-kind"
        src={STATIC_CONTENTS.lineBeKind[language].src}
        imgRatio={STATIC_CONTENTS.lineBeKind[language].ratio}
        lazy={false}
      />
      <ResponsiveImage
        className="give-back"
        src={STATIC_CONTENTS.giveBack[language].src}
        imgRatio={STATIC_CONTENTS.giveBack[language].ratio}
        lazy={false}
      />
      <span className={clsx(Typo.body2, 'text-give-back-body')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_YELLOW_FIRST_BODY',
          `These are unprecedented times.

We are humble to see how the world pulls together at a time like this. At Pomelo, our employees and Pomelo community are our number one priority. We are all in this TOGETHER.

With that in mind, we want to do our part to give back to our local communitles and do what wa can to support those that are fighting Covid-19 on the front line.`,
        )}
      </span>
      <span className={clsx(Typo.subtitle2, 'text-give-back-subbody')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_YELLOW_GIVE_BACK_SUBBODY',
          'Please join us in our #Pomelocares initiatives',
        )}
      </span>
      <ResponsiveImage
        className="icons-set"
        src={STATIC_CONTENTS.iconsSet.src}
        imgRatio={STATIC_CONTENTS.iconsSet.ratio}
        lazy={false}
      />
      <span className={clsx(Typo.caption, 'text-give-back-hashtag')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_YELLOW_GIVE_BACK_HASHTAG',
          '#POMELOCARES',
        )}
      </span>
      <ResponsiveImage
        className="line-spread-love"
        src={STATIC_CONTENTS.lineSpreadLove[language].src}
        imgRatio={STATIC_CONTENTS.lineSpreadLove[language].ratio}
        lazy={false}
      />
      <ResponsiveImage
        className="donate-now"
        src={STATIC_CONTENTS.donateNow[language].src}
        imgRatio={STATIC_CONTENTS.donateNow[language].ratio}
        lazy={false}
      />
      <span className={clsx(Typo.body2, 'text-donate-body')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_YELLOW_DONATE_BODY',
          `A little kindness goes a long way.

We are working with charity partners in your community, who provide vital care and support for those in need

Every little bit helps!`,
        )}
      </span>
      <div className="jar-heart" ref={jarHeartRef} />
      <div className={clsx(Typo.h4, 'text-amount-value')}>{amount}</div>
      <span className={clsx(Typo.subtitle2, 'text-amount-label')}>
        {hasLocizeTranslation(
          t,
          'POMELO_CARE_YELLOW_AMOUNT_LABEL',
          'total donated',
        )}
      </span>
      <a
        className="click-donate webview-cta"
        href={STATIC_CONTENTS.paypalLinks[country][language]}
        target="_self"
        rel="nofollow"
        onClick={onPaypalLinkClicked}
      >
        <ResponsiveImage
          alt="donate via paypal"
          src={STATIC_CONTENTS.clickDonate[language].src}
          imgRatio={STATIC_CONTENTS.clickDonate[language].ratio}
          lazy={false}
          phone="50vw"
        />
      </a>
    </section>
  )
}

YellowSection.defaultProps = {
  amount: '',
  isDesktop: false,
  isLottieReady: false,
  isThankYouPage: false,
}

YellowSection.propTypes = {
  amount: PropTypes.string,
  country: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool,
  isLottieReady: PropTypes.bool,
  isThankYouPage: PropTypes.bool,
  language: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}

export default YellowSection
